import { CloseButton, Combobox, Divider, FocusTrap, Input, InputWrapper, Tooltip, useCombobox } from '@mantine/core';
import { Bell } from 'lucide-react';
import { FC, ReactElement, useMemo, useState } from 'react';

import { useAutoSelectSoleOption } from '@/hooks/use-auto-select-sole-option.ts';
import useAnimatedIcon from '@/hooks/useAnimatedIcon.tsx';
import useAuth from '@/hooks/useAuth.ts';

import { AnimationStyle } from '@/models/Animation.ts';
import { UserGroup } from '@/models/User.ts';
import { AppointmentTypeFormOption } from '@/models/appointment/BookingFormOptions.ts';

import { cn } from '@/utils/styleUtils.ts';

interface Props {
    data: AppointmentTypeFormOption[];
    defaultAptTypeIdOrScheduleTypeId?: string;
    label: string;
    placeholder?: string;
    setSelectedAppointmentTypeId: (value: string | undefined) => void;
    disabled?: boolean;
    autoFocusActive: boolean;
}

const getAppointmentById = (data: AppointmentTypeFormOption[], id: string): AppointmentTypeFormOption | undefined => {
    return data.find((a) => a.value === id);
};

const getAppointmentByName = (
    data: AppointmentTypeFormOption[],
    name: string
): AppointmentTypeFormOption | undefined => {
    return data.find((a) => a.label === name);
};

const getSearchFromDefault = (data: AppointmentTypeFormOption[], default_id?: string): string => {
    if (!default_id) return '';
    const matched = getAppointmentById(data, default_id);
    return matched?.label ?? '';
};

export const CustomAppointmentTypeSelect: FC<Props> = ({
    data,
    defaultAptTypeIdOrScheduleTypeId,
    label,
    setSelectedAppointmentTypeId,
    placeholder,
    disabled,
    autoFocusActive = false,
}) => {
    const { isUserInGroup } = useAuth();
    const { animatedComponentId, setId, animationClass, baseClass, styleElement } = useAnimatedIcon(
        AnimationStyle.BELL_WIGGLE,
        0.001
    );

    const isRenderNotification = isUserInGroup([
        UserGroup.DEV_INTERNAL_ENGINEERS,
        UserGroup.PROD_INTERNAL_ENGINEERS,
        UserGroup.INTERNAL_PRODUCT_MANAGER,
        UserGroup.DEV_INTERNAL_QA,
        UserGroup.PROD_INTERNAL_QA,
    ]);

    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const [search, setSearch] = useState<string>(() => getSearchFromDefault(data, defaultAptTypeIdOrScheduleTypeId));

    // Validate search against current data, reset if invalid
    const [prevData, setPrevData] = useState(data);
    if (data !== prevData) {
        setPrevData(data);
        if (search && !getAppointmentByName(data, search)) {
            setSearch('');
        }
    }

    // `is_offered` is undefined until a calendar is picked, so restricting to offered types also
    // keeps the mount cascade in order: calendar first, then the type it actually offers.
    useAutoSelectSoleOption({
        options: data.filter((option) => option.is_offered === true),
        getKey: (option) => option.value,
        hasSelection: search !== '',
        onSelect: (option) => {
            setSearch(option.label);
            setSelectedAppointmentTypeId(option.value);
            combobox.closeDropdown();
        },
        isEnabled: !disabled,
    });

    // Derive is_offered from current search
    const isOffered = search ? getAppointmentByName(data, search)?.is_offered : undefined;

    // Filter and split groups into categories in a single pass
    const { mappedGroups, unmappedGroups, notOfferedGroups } = useMemo(() => {
        const searchTerm = search.toLowerCase().trim();
        const mapped: AppointmentTypeFormOption[] = [];
        const unmapped: AppointmentTypeFormOption[] = [];
        const notOffered: AppointmentTypeFormOption[] = [];

        for (const aptOrSchedule of data) {
            if (!aptOrSchedule.label.toLowerCase().includes(searchTerm)) {
                continue;
            }

            if (!aptOrSchedule.is_offered) {
                notOffered.push(aptOrSchedule);
            } else if (aptOrSchedule.is_mapped) {
                mapped.push(aptOrSchedule);
            } else {
                unmapped.push(aptOrSchedule);
            }
        }

        return { mappedGroups: mapped, unmappedGroups: unmapped, notOfferedGroups: notOffered };
    }, [data, search]);

    // Handler for clearing all selection states
    const handleClear = (): void => {
        setSelectedAppointmentTypeId(undefined);
        setSearch('');
        combobox.closeDropdown();
    };

    const getGroups = (): ReactElement => (
        <>
            {styleElement} {/* Used to animate the bell icon */}
            <Combobox.Group key="mapped">
                {mappedGroups.map((a) => (
                    <Combobox.Option value={a.value} key={a.value}>
                        <div
                            className="flex items-center justify-between text-sm"
                            onMouseEnter={() => setId(a.value)}
                            onMouseLeave={() => setId(undefined)}
                        >
                            {a.label}
                            {isRenderNotification && a.is_notification_enabled && (
                                <Tooltip label="Patient will receive a notification">
                                    <Bell
                                        className={cn(
                                            'ml-2 size-2.5 flex-shrink-0 origin-top',
                                            baseClass,
                                            animatedComponentId === a.value && animationClass
                                        )}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    </Combobox.Option>
                ))}
            </Combobox.Group>
            {unmappedGroups.length > 0 && <Divider className="my-1" />}
            <Combobox.Group key="unmapped">
                {unmappedGroups.map((a) => (
                    <Combobox.Option value={a.value} key={a.value}>
                        <div className={cn('text-sm')}>{a.label}</div>
                    </Combobox.Option>
                ))}
            </Combobox.Group>
            {notOfferedGroups.length > 0 && <Divider className="my-1" />}
            <Combobox.Group key="not-offered">
                {notOfferedGroups.map((a) => (
                    <Combobox.Option value={a.value} key={a.value}>
                        <div className={cn('text-sm', 'text-slate-500')}>{a.label}</div>
                    </Combobox.Option>
                ))}
            </Combobox.Group>
        </>
    );

    const groups = getGroups();

    return (
        <FocusTrap active={autoFocusActive}>
            <InputWrapper
                label={label}
                error={
                    isOffered == false ? 'Bitte wählen Sie eine andere Behandler:in, um diesen Termin zu buchen' : ''
                }
                classNames={{
                    error: 'text-orange-600',
                    label: 'font-bold',
                }}
            >
                <Combobox
                    classNames={{
                        groupLabel: 'text-sm font-semibold text-black',
                    }}
                    store={combobox}
                    withinPortal={false}
                    onOptionSubmit={(id) => {
                        const aptSelected = getAppointmentById(data, id);
                        setSelectedAppointmentTypeId(id);
                        setSearch(aptSelected?.label ?? '');
                        combobox.closeDropdown();
                    }}
                >
                    <Combobox.Target>
                        <Input
                            classNames={{
                                input: cn(isOffered == false && 'border-orange-600'),
                            }}
                            disabled={disabled}
                            value={search}
                            onChange={(event) => {
                                combobox.openDropdown();
                                combobox.updateSelectedOptionIndex();
                                setSearch(event.currentTarget.value);
                            }}
                            onClick={() => combobox.openDropdown()}
                            onFocus={() => combobox.openDropdown()}
                            onBlur={() => combobox.closeDropdown()}
                            placeholder={placeholder}
                            rightSection={
                                search === '' ? (
                                    <Combobox.Chevron />
                                ) : (
                                    <CloseButton
                                        size="sm"
                                        disabled={disabled}
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={handleClear}
                                        aria-label="Clear value"
                                    />
                                )
                            }
                            rightSectionPointerEvents={search === '' || disabled ? 'none' : 'all'}
                        />
                    </Combobox.Target>

                    <Combobox.Dropdown className="shadow-sm">
                        <Combobox.Options className="max-h-[200px] overflow-y-auto">
                            {mappedGroups.length + unmappedGroups.length + notOfferedGroups.length > 0 ? (
                                groups
                            ) : (
                                <Combobox.Empty>Nothing found</Combobox.Empty>
                            )}
                        </Combobox.Options>
                    </Combobox.Dropdown>
                </Combobox>
            </InputWrapper>
        </FocusTrap>
    );
};
